import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoClose,
  IoTimeOutline,
  IoPersonOutline,
  IoCalendarOutline,
  IoBookOutline,
  IoMedkitOutline,
  IoPencilOutline,
} from "react-icons/io5";
import { format } from "date-fns";
import { Schedule } from "../../../types/types";
import ContentSelector from "./ContentSelector";
import accessURL from "../../../../../../components/utils/access";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store";

interface ReportModalProps {
  schedule: Schedule | null;
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  dateOfVisit: string;
  objective: string | null;
  content: number[];
  products: number[];
  followupPlan: string;
  customer: string | null;
  timeOfVisit: string;
}

const formatTimeString = (timeString: string): string => {
  return format(new Date(`1970-01-01T${timeString}`), "hh:mm a");
};

const ReportModal: React.FC<ReportModalProps> = ({
  schedule,
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState<FormData>({
    dateOfVisit: "",
    objective: null,
    content: [],
    products: [],
    followupPlan: "",
    customer: null,
    timeOfVisit: "",
  });

  const [contentError, setContentError] = useState<string>("");
  const userInfo = useSelector((state: RootState) => state.user.user?.userInfo);
  const email = userInfo?.customID || "";

  const navigate = useNavigate();
  // Check if all required fields are filled
  const isFormValid = () => {
    return (
      formData.dateOfVisit !== "" &&
      formData.customer !== null &&
      formData.objective !== null &&
      formData.content.length > 0
    );
  };

  // Set initial values when schedule changes
  useEffect(() => {
    if (schedule) {
      const formattedStartTime = formatTimeString(schedule.startTime);
      const formattedEndTime = formatTimeString(schedule.endTime);
      const timeOfVisit = `${formattedStartTime} - ${formattedEndTime}`;

      // Parse the products string into an array
      const productsArray = schedule.products
        ? JSON.parse(schedule.products)
        : [];

      setFormData((prev) => ({
        ...prev,
        dateOfVisit: schedule.dateOfVisit || "",
        customer: schedule.customer || null,
        objective: schedule.objective || null,
        timeOfVisit: timeOfVisit,
        products: productsArray,
      }));
    }
  }, [schedule]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.content.length === 0) {
      setContentError("Please select at least one content");
      return;
    }

    // Clear any previous error
    setContentError("");

    const response = await accessURL.post("/reports/create", formData);

    if (response.status === 201) {
      toast.success("Report created successfully!");
      setTimeout(() => {
        navigate(`/salesRep/${email}/reports`);
        onClose();
      }, 2000);
      //   setIsLoading(false);
    } else if (response.status === 409 || response.data?.code === 409) {
      toast.error(
        response.data?.message || "Report already created for this customer"
      );
    } else {
      toast.error("Failed to create report. Please try again.");
      //   setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && schedule && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ duration: 0.2 }}
                className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl transform max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 z-50"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">
                      Schedule Report
                    </h2>
                    <button
                      onClick={onClose}
                      className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                      type="button"
                      aria-label="Close modal"
                    >
                      <IoClose className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                </motion.div>

                <form onSubmit={handleSubmit}>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 gap-6">
                      {/* Date of Visit */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Visit
                        </label>
                        <div className="relative">
                          <input
                            type="date"
                            className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                            value={formData.dateOfVisit}
                            readOnly
                          />
                          <IoCalendarOutline className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>
                      </div>

                      {/* Customer */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                            value={formData.customer || ""}
                            readOnly
                          />
                          <IoPersonOutline className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>
                      </div>

                      {/* Time of Visit */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Time of Visit
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                            value={formData.timeOfVisit}
                            readOnly
                          />
                          <IoTimeOutline className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>
                      </div>

                      {/* Objective */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Objective
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                            value={formData.objective || ""}
                            readOnly
                          />
                          <IoBookOutline className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>
                      </div>

                      {/* Products */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Products
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                            value={formData.products.join(", ")}
                            readOnly
                          />
                          <IoMedkitOutline className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>
                      </div>

                      {/* Content Selector */}
                      <ContentSelector
                        selectedContents={formData.content}
                        onContentsChange={(contents) => {
                          setFormData((prev) => ({
                            ...prev,
                            content: contents,
                          }));
                          setContentError("");
                        }}
                        error={contentError}
                      />

                      {/* Follow-up Plan */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Follow-up Plan
                        </label>
                        <div className="relative">
                          <textarea
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] resize-y"
                            value={formData.followupPlan}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                followupPlan: e.target.value,
                              }))
                            }
                            placeholder="Enter your follow-up plan..."
                          />
                          <IoPencilOutline className="absolute right-3 top-3 text-gray-400 h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200"
                  >
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        disabled={!isFormValid()}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isFormValid()
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-300 cursor-not-allowed"
                        }`}
                      >
                        Create
                      </button>
                    </div>
                  </motion.div>
                </form>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;
